if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.form.submit = this.onSubmitHandler.bind(this);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        ProductForm._installRedirectBlocker();
      }

      static _installRedirectBlocker() {
        if (window._cartRedirectBlockerInstalled) return;
        window._cartRedirectBlockerInstalled = true;
        window._blockCartRedirect = false;

        // Navigation API — intercepts ALL navigations including window.location = url
        if (typeof navigation !== 'undefined' && navigation.addEventListener) {
          navigation.addEventListener('navigate', (event) => {
            try {
              if (!window._blockCartRedirect) return;
              const pathname = new URL(event.destination.url).pathname;
              if (pathname === '/cart' || pathname === '/cart/') {
                event.preventDefault();
              }
            } catch (e) {}
          });
        }

        // Fallback: intercept location.assign and location.replace
        try {
          const origAssign = Location.prototype.assign;
          Location.prototype.assign = function (url) {
            if (window._blockCartRedirect && /\/cart(\?|#|\/|$)/.test(String(url))) return;
            return origAssign.call(this, url);
          };
          const origReplace = Location.prototype.replace;
          Location.prototype.replace = function (url) {
            if (window._blockCartRedirect && /\/cart(\?|#|\/|$)/.test(String(url))) return;
            return origReplace.call(this, url);
          };
        } catch (e) {}
      }

      onSubmitHandler(evt) {
        if (evt && evt.preventDefault) evt.preventDefault();
        if (evt && evt.stopPropagation) evt.stopPropagation();
        if (evt && evt.stopImmediatePropagation) evt.stopImmediatePropagation();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        // Block any JS redirect to /cart during the add-to-cart window
        window._blockCartRedirect = true;

        if (!this.cart) {
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        }

        // A third-party bundle widget (e.g. Vital Bundles) posts its own
        // /cart/add request for this form. Posting ours too would add the
        // item twice, so let it own the add and just refresh the cart UI
        // once its request succeeds.
        if (document.querySelector('input.burr_bundle_pair[name="cb"]')) {
          this.waitForExternalCartAdd();
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
              if (!this.cart) return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cartData: response });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
            // Release the redirect block after drawer has had time to open
            setTimeout(() => { window._blockCartRedirect = false; }, 3000);
          });
      }

      waitForExternalCartAdd() {
        const finish = () => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          setTimeout(() => {
            window._blockCartRedirect = false;
          }, 3000);
        };

        const onAdded = () => {
          document.removeEventListener('theme:cart-add-success', onAdded);
          clearTimeout(timeoutId);

          if (!this.cart) {
            finish();
            return;
          }

          const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
          fetch(`${window.Shopify.routes.root}?sections=${sectionIds.join(',')}`)
            .then((response) => response.json())
            .then((sections) => {
              this.cart.renderContents({ sections });
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(finish);
        };

        const timeoutId = setTimeout(() => {
          document.removeEventListener('theme:cart-add-success', onAdded);
          finish();
        }, 8000);

        document.addEventListener('theme:cart-add-success', onAdded);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
